import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Mail, Phone, Linkedin, Github,
  GraduationCap, Target, Eye, Heart,
} from 'lucide-react';
import Logo from '@/components/Logo';

// ─── Medium SVG icon ──────────────────────────────────────────────────────────
function MediumIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zm7.42 0c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z" />
    </svg>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────
function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 16 }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#14B8A6' }} />
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9, letterSpacing: '.18em',
        color: 'rgba(20,184,166,.7)',
      }}>{text}</span>
    </div>
  );
}

function SocialBtn({ href, title, children }: { href: string; title: string; children: React.ReactNode }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel="noopener noreferrer"
      title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 32, height: 32, borderRadius: 8,
        border: `1px solid ${hov ? 'rgba(255,255,255,.3)' : 'rgba(255,255,255,.1)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : 'rgba(255,255,255,.4)',
        textDecoration: 'none',
        transition: 'border-color .2s, color .2s', flexShrink: 0,
      }}
    >
      {children}
    </a>
  );
}

const base = {
  background: '#0A0A0A',
  fontFamily: "'Outfit', sans-serif",
  color: '#fff',
  minHeight: '100vh',
};

const divider = { borderBottom: '1px solid rgba(255,255,255,.07)' };
const pad = { padding: 'clamp(48px,6vw,72px) clamp(24px,5vw,56px)' };

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function About() {
  const navigate = useNavigate();

  return (
    <div style={base}>

      {/* ── Sticky nav ──────────────────────────────────── */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px clamp(24px,5vw,56px)',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 50,
      }}>
        <Logo size="md" clickable={true} navigateTo="/" scheduleWhite={true} />
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: '#14B8A6', fontWeight: 500,
            background: 'transparent', border: '1px solid rgba(20, 184, 166, 0.5)',
            borderRadius: 0, padding: '10px 18px',
            fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
            transition: 'background .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(20, 184, 166, 0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <ArrowLeft size={15} aria-hidden /> Back to Home
        </button>
      </header>

      {/* ── Hero ────────────────────────────────────────── */}
      <section style={{ ...pad, ...divider, position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -80, right: -60, width: 400, height: 400,
          background: 'radial-gradient(circle,rgba(20,184,166,.1) 0%,transparent 65%)',
          pointerEvents: 'none',
        }} />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          style={{ position: 'relative', zIndex: 1, maxWidth: 720 }}
        >
          <SectionLabel text="ABOUT US" />
          <h1 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 'clamp(48px,7vw,80px)', fontWeight: 400,
            letterSpacing: '-.03em', lineHeight: .95, marginBottom: 24,
          }}>
            Building the<br />
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.3)' }}>campus of</em><br />
            tomorrow
          </h1>
          <p style={{
            fontSize: 14, lineHeight: 1.75, color: 'rgba(255,255,255,.4)',
            fontWeight: 300, maxWidth: 480,
          }}>
            A modern, student-first platform designed to simplify university life and make
            schedule management effortless — built by a student who lived the problem.
          </p>
        </motion.div>
      </section>

      {/* ── Founder ─────────────────────────────────────── */}
      <section style={{ ...pad, ...divider }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionLabel text="THE FOUNDER" />
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-8 sm:gap-12 items-start" style={{ marginTop: 36 }}>
            {/* Photo / avatar */}
            <div style={{
              width: 160, height: 160, borderRadius: 20, flexShrink: 0,
              background: 'linear-gradient(135deg,rgba(20,184,166,.3),rgba(96,165,250,.2))',
              border: '1px solid rgba(20,184,166,.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <img
                src="/founder.png"
                alt="Faisal Hakimi"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 28, fontWeight: 500, color: '#14B8A6',
                position: 'absolute',
              }}>FH</span>
            </div>

            <div>
              <h2 style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: 'clamp(30px, 6vw, 42px)', fontWeight: 400,
                letterSpacing: '-.02em', lineHeight: 1, marginBottom: 6,
              }}>Faisal Hakimi</h2>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, letterSpacing: '.14em',
                color: 'rgba(20,184,166,.7)', marginBottom: 20,
              }}>FOUNDER & DEVELOPER</div>

              <p style={{
                fontSize: 13, lineHeight: 1.8,
                color: 'rgba(255,255,255,.45)', fontWeight: 300,
                maxWidth: 520, marginBottom: 24,
              }}>
                Machine Learning Engineer and Researcher focused on transitioning state-of-the-art models
                from academic environments into low-latency, scalable production architectures. Core competencies
                in Bayesian Optimization, Edge AI Deployment, and LLM Orchestration.
              </p>

              {/* Tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                {['BCS 2022–2026', 'AI LEAD · VERTEXONS', 'CO-FOUNDER · STUMBNAIL', 'ML RESEARCHER'].map(t => (
                  <span key={t} style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9, letterSpacing: '.1em',
                    padding: '4px 10px', borderRadius: 4,
                    border: '1px solid rgba(255,255,255,.1)',
                    color: 'rgba(255,255,255,.4)',
                  }}>{t}</span>
                ))}
              </div>

              {/* Socials */}
              <div style={{ display: 'flex', gap: 8 }}>
                <SocialBtn href="mailto:info@unischedule.me" title="Email"><Mail size={14} /></SocialBtn>
                <SocialBtn href="tel:+923109931126" title="Phone"><Phone size={14} /></SocialBtn>
                <SocialBtn href="https://www.linkedin.com/in/faisal-hakimi55/" title="LinkedIn"><Linkedin size={14} /></SocialBtn>
                <SocialBtn href="https://github.com/Faisalhakimi22" title="GitHub"><Github size={14} /></SocialBtn>
                <SocialBtn href="https://medium.com/@faisalh5556" title="Medium"><MediumIcon size={14} /></SocialBtn>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Story ───────────────────────────────────────── */}
      <section
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 lg:gap-16 items-start"
        style={{ ...divider, padding: 'clamp(48px,6vw,72px) clamp(24px,5vw,56px)' }}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionLabel text="OUR STORY" />
          <h2 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 'clamp(28px,4vw,42px)', fontWeight: 400,
            letterSpacing: '-.02em', lineHeight: 1, marginTop: 16,
          }}>
            Built by a<br />student,{' '}
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.3)' }}>for</em>
            <br />students
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 36 }}
        >
          {[
            { text: 'UniSchedule was born out of a simple frustration: managing university schedules shouldn\'t be complicated. As a CS student, I experienced firsthand the challenges of keeping track of classes, finding rooms, and coordinating with classmates.', highlight: true },
            { text: 'What started as a personal project to solve my own scheduling problems has evolved into a comprehensive platform that helps thousands of students stay organized. Every feature is designed with real student needs in mind, because I\'ve been there too.' },
            { text: 'Today, UniSchedule continues to grow and improve, driven by feedback from students like you. Together, we\'re making university life a little bit easier, one schedule at a time.' },
          ].map(({ text, highlight }, i) => (
            <p key={i} style={{
              fontSize: 13, lineHeight: 1.85, fontWeight: 300,
              color: highlight ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.4)',
              paddingLeft: 16,
              borderLeft: `2px solid ${highlight ? '#14B8A6' : 'rgba(255,255,255,.08)'}`,
            }}>{text}</p>
          ))}
        </motion.div>
      </section>

      {/* ── Mission / Vision / Values ───────────────────── */}
      <section style={{ ...pad, ...divider }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionLabel text="MISSION & VALUES" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" style={{ marginTop: 36 }}>
            {[
              { Icon: Target, title: 'Our Mission', text: 'Empower students with intuitive tools that make managing their academic schedules simple and stress-free.' },
              { Icon: Eye,    title: 'Our Vision',  text: 'Become the go-to platform for university students, helping them stay organized and focused on what truly matters.' },
              { Icon: Heart,  title: 'Our Values',  text: 'Student-first design, continuous improvement, and building features that genuinely make a difference.' },
            ].map(({ Icon, title, text }) => (
              <MissionCard key={title} Icon={Icon} title={title} text={text} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Why UniSchedule ─────────────────────────────── */}
      <section style={{ ...pad, ...divider }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionLabel text="WHY UNISCHEDULE" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" style={{ marginTop: 36 }}>
            {[
              { num: '01', title: 'Student-First Design',  desc: 'Every feature designed based on real student feedback and needs.' },
              { num: '02', title: 'Always Improving',       desc: 'Regular updates and new features based on your suggestions.' },
              { num: '03', title: 'Free to Use',            desc: 'No hidden fees, no premium tiers — just helpful tools for students.' },
              { num: '04', title: 'Privacy Focused',        desc: 'Your data is yours. We protect it with 256-bit encryption.' },
              { num: '05', title: 'Mobile Friendly',        desc: 'Access your schedule anywhere, on any device, at any time.' },
              { num: '06', title: 'Community Driven',       desc: 'Built by students, for students, shaped by real feedback.' },
            ].map(c => <WhyCard key={c.num} {...c} />)}
          </div>
        </motion.div>
      </section>

      {/* ── CTA ─────────────────────────────────────────── */}
      <section style={{
        ...pad, textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,.07)',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 'clamp(32px,5vw,56px)', fontWeight: 400,
            letterSpacing: '-.03em', lineHeight: 1, marginBottom: 16,
          }}>
            Ready to{' '}
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.3)' }}>get</em>
            <br />started?
          </h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,.4)', fontWeight: 300, marginBottom: 32 }}>
            Join thousands of students already using UniSchedule
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
            <CtaBtn primary={true} onClick={() => navigate('/student-form')}>
              <GraduationCap size={15} aria-hidden /> Get Started Free
            </CtaBtn>
            <CtaBtn primary={false} onClick={() => navigate('/contact')}>Contact Us</CtaBtn>
          </div>
        </motion.div>
      </section>

      {/* ── Footer ──────────────────────────────────────── */}
      <footer style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px clamp(24px,5vw,56px)',
      }}>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', color: 'rgba(255,255,255,.2)' }}>
          © 2025 UNISCHEDULE
        </span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', color: 'rgba(255,255,255,.2)' }}>
          BUILT WITH ♥ BY{' '}
          <a
            href="https://hakimi.tech"
            target="_blank"
            rel="noopener noreferrer"
            title="hakimi.tech"
            style={{ color: '#14B8A6', textDecoration: 'underline', textDecorationColor: 'rgba(20,184,166,.4)', textUnderlineOffset: 3, cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', transition: 'color .2s, text-decoration-color .2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#2DD4BF'; e.currentTarget.style.textDecorationColor = '#2DD4BF'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#14B8A6'; e.currentTarget.style.textDecorationColor = 'rgba(20,184,166,.4)'; }}
          >FAISAL HAKIMI</a>
        </span>
      </footer>

    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function MissionCard({ Icon, title, text }: { Icon: any; title: string; text: string }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        border: `1px solid ${hov ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.07)'}`,
        borderRadius: 14, padding: '28px 24px',
        transition: 'border-color .2s, background .2s',
        background: hov ? 'rgba(255,255,255,.05)' : 'rgba(255,255,255,.03)',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: 'rgba(20,184,166,.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#14B8A6', marginBottom: 16,
      }}>
        <Icon size={17} aria-hidden />
      </div>
      <h3 style={{
        fontFamily: "'Instrument Serif', serif",
        fontSize: 22, fontWeight: 400,
        letterSpacing: '-.01em', marginBottom: 10,
      }}>{title}</h3>
      <p style={{ fontSize: 12, lineHeight: 1.75, color: 'rgba(255,255,255,.4)', fontWeight: 300 }}>{text}</p>
    </div>
  );
}

function WhyCard({ num, title, desc }: { num: string; title: string; desc: string }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'rgba(255,255,255,.03)',
        border: `1px solid ${hov ? 'rgba(20,184,166,.25)' : 'rgba(255,255,255,.07)'}`,
        borderRadius: 12, padding: '20px 22px',
        transition: 'border-color .2s',
      }}
    >
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '.12em', color: 'rgba(20,184,166,.5)', marginBottom: 10 }}>{num}</div>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, lineHeight: 1.7, color: 'rgba(255,255,255,.4)', fontWeight: 300 }}>{desc}</div>
    </div>
  );
}

function CtaBtn({ children, onClick, primary }: { children: React.ReactNode; onClick: () => void; primary: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontSize: 13, fontWeight: 500,
        fontFamily: "'Outfit', sans-serif",
        padding: '12px 24px', borderRadius: 0, cursor: 'pointer',
        transition: 'opacity .2s, background .2s',
        ...(primary
          ? { background: '#14B8A6', color: '#0A0A0A', border: 'none', opacity: hov ? .85 : 1 }
          : {
              background: hov ? 'rgba(20, 184, 166, 0.1)' : 'transparent',
              color: '#14B8A6',
              border: '1px solid rgba(20, 184, 166, 0.5)',
            }
        ),
      }}
    >
      {children}
    </button>
  );
}